import React, { useEffect, useMemo, useRef, useState } from 'react'
import cx from 'classnames'

import { Image } from 'src/components/image'

export type VideoSourceType = 'external' | 'sanityFile'
export type VideoProvider = 'auto' | 'youtube' | 'vimeo' | 'dropbox' | 'direct'
export type VideoAspectRatio = '16:9' | '4:3' | '1:1' | '9:16' | '21:9'
export type VideoDisplay = 'container' | 'wide' | 'fullWidth'
export type VideoCornerRadius = 'none' | 'sm' | 'md' | 'lg'
export type VideoObjectFit = 'cover' | 'contain'

type SanityAsset = {
  _id?: string
  _ref?: string
  url?: string
  mimeType?: string
}

export type VideoFile = {
  asset?: SanityAsset
}

export type VideoPosterImage = {
  alt?: string
  asset?: SanityAsset
}

export interface VideoPlayerProps {
  sourceType?: VideoSourceType
  provider?: VideoProvider
  url?: string
  file?: VideoFile
  legacyYoutubeUrl?: string
  legacyVimeoUrl?: string
  legacyDirectUrl?: string
  posterImage?: VideoPosterImage
  posterAlt?: string
  title?: string
  autoPlay?: boolean
  muted?: boolean
  loop?: boolean
  showControls?: boolean
  aspectRatio?: VideoAspectRatio
  mobileAspectRatio?: VideoAspectRatio
  display?: VideoDisplay
  cornerRadius?: VideoCornerRadius
  objectFit?: VideoObjectFit
  className?: string
}

type ResolvedVideo =
  | {
      kind: 'iframe'
      provider: 'youtube' | 'vimeo'
      src: string
      title: string
    }
  | {
      kind: 'native'
      provider: 'dropbox' | 'direct' | 'sanityFile'
      src: string
      mimeType?: string
    }
  | {
      kind: 'missing'
    }

const aspectRatioClassNames: Record<string, string> = {
  '16:9': '16-9',
  '4:3': '4-3',
  '1:1': '1-1',
  '9:16': '9-16',
  '21:9': '21-9',
}

const displayClassNames: Record<string, string> = {
  container: 'container',
  wide: 'wide',
  fullWidth: 'fullWidth',
}

const cornerRadiusClassNames: Record<string, string> = {
  none: 'none',
  sm: 'sm',
  md: 'md',
  lg: 'lg',
}

const objectFitClassNames: Record<string, string> = {
  cover: 'cover',
  contain: 'contain',
}

const getOptionClassName = (
  options: Record<string, string>,
  value: string | undefined,
  fallback: string
) => options[value || fallback] || options[fallback]

const isHttpUrl = (url: URL) =>
  url.protocol === 'http:' || url.protocol === 'https:'

const parseUrl = (url?: string) => {
  const value = url?.trim()

  if (!value) return undefined

  try {
    const parsedUrl = new URL(value)

    return isHttpUrl(parsedUrl) ? parsedUrl : undefined
  } catch (_error) {
    try {
      const parsedUrl = new URL(`https://${value}`)

      return isHttpUrl(parsedUrl) ? parsedUrl : undefined
    } catch (_withProtocolError) {
      return undefined
    }
  }
}

const normalizeHost = (url: URL) => url.hostname.replace(/^www\./, '')

const isAllowedHost = (host: string, domain: string) =>
  host === domain || host.endsWith(`.${domain}`)

const getYoutubeId = (url: string) => {
  const parsedUrl = parseUrl(url)

  if (!parsedUrl) return undefined

  const host = normalizeHost(parsedUrl)
  const pathParts = parsedUrl.pathname.split('/').filter(Boolean)

  if (isAllowedHost(host, 'youtu.be')) {
    return pathParts[0]
  }

  if (isAllowedHost(host, 'youtube.com')) {
    if (parsedUrl.searchParams.get('v')) {
      return parsedUrl.searchParams.get('v') || undefined
    }

    if (pathParts[0] === 'embed' || pathParts[0] === 'shorts') {
      return pathParts[1]
    }
  }

  return undefined
}

const getVimeoId = (url: string) => {
  const parsedUrl = parseUrl(url)

  if (!parsedUrl) return undefined
  if (!isAllowedHost(normalizeHost(parsedUrl), 'vimeo.com')) return undefined

  const pathParts = parsedUrl.pathname.split('/').filter(Boolean)

  return [...pathParts].reverse().find(part => /^\d+$/.test(part))
}

const detectProvider = (url: string, provider: VideoProvider) => {
  if (provider !== 'auto') return provider

  const parsedUrl = parseUrl(url)
  const host = parsedUrl ? normalizeHost(parsedUrl) : ''

  if (isAllowedHost(host, 'youtu.be') || isAllowedHost(host, 'youtube.com')) {
    return 'youtube'
  }

  if (isAllowedHost(host, 'vimeo.com')) {
    return 'vimeo'
  }

  if (
    isAllowedHost(host, 'dropbox.com') ||
    isAllowedHost(host, 'dropboxusercontent.com')
  ) {
    return 'dropbox'
  }

  return 'direct'
}

const normalizeDropboxUrl = (url: string) => {
  const parsedUrl = parseUrl(url)

  if (!parsedUrl) return url

  if (isAllowedHost(normalizeHost(parsedUrl), 'dropboxusercontent.com')) {
    return parsedUrl.toString()
  }

  parsedUrl.searchParams.delete('dl')
  parsedUrl.searchParams.set('raw', '1')

  return parsedUrl.toString()
}

const getNativeMimeType = (url: string, mimeType?: string) => {
  if (mimeType) return mimeType

  const pathname = parseUrl(url)?.pathname || url
  const extension = pathname.split('.').pop()?.toLowerCase()

  switch (extension) {
    case 'mp4':
      return 'video/mp4'
    case 'webm':
      return 'video/webm'
    case 'ogv':
    case 'ogg':
      return 'video/ogg'
    case 'mov':
      return 'video/quicktime'
    default:
      return undefined
  }
}

const buildYoutubeUrl = (
  url: string,
  autoPlay: boolean,
  muted: boolean,
  loop: boolean,
  showControls: boolean
) => {
  const videoId = getYoutubeId(url)

  if (!videoId) return undefined

  const embedUrl = new URL(`https://www.youtube.com/embed/${videoId}`)
  embedUrl.searchParams.set('rel', '0')
  embedUrl.searchParams.set('modestbranding', '1')
  embedUrl.searchParams.set('playsinline', '1')

  if (autoPlay) embedUrl.searchParams.set('autoplay', '1')
  if (muted) embedUrl.searchParams.set('mute', '1')
  if (!showControls) embedUrl.searchParams.set('controls', '0')

  if (loop) {
    embedUrl.searchParams.set('loop', '1')
    embedUrl.searchParams.set('playlist', videoId)
  }

  return embedUrl.toString()
}

const buildVimeoUrl = (
  url: string,
  autoPlay: boolean,
  muted: boolean,
  loop: boolean,
  showControls: boolean
) => {
  const videoId = getVimeoId(url)
  const originalUrl = parseUrl(url)

  if (!videoId) return undefined

  const embedUrl = new URL(`https://player.vimeo.com/video/${videoId}`)
  const pathParts = originalUrl?.pathname.split('/').filter(Boolean) || []
  const videoIdIndex = pathParts.indexOf(videoId)
  embedUrl.searchParams.set('title', '0')
  embedUrl.searchParams.set('byline', '0')
  embedUrl.searchParams.set('portrait', '0')
  embedUrl.searchParams.set('dnt', '1')

  const privateHash =
    originalUrl?.searchParams.get('h') ||
    (videoIdIndex >= 0 ? pathParts[videoIdIndex + 1] : undefined)
  if (privateHash) embedUrl.searchParams.set('h', privateHash)

  if (autoPlay) embedUrl.searchParams.set('autoplay', '1')
  if (muted) embedUrl.searchParams.set('muted', '1')
  if (loop) embedUrl.searchParams.set('loop', '1')
  if (!showControls) embedUrl.searchParams.set('controls', '0')

  return embedUrl.toString()
}

const resolveVideo = ({
  sourceType = 'external',
  provider = 'auto',
  url,
  file,
  legacyYoutubeUrl,
  legacyVimeoUrl,
  legacyDirectUrl,
  title,
  autoPlay,
  muted,
  loop,
  showControls,
}: VideoPlayerProps): ResolvedVideo => {
  const shouldAutoPlay = !!autoPlay
  const shouldMute = !!muted
  const shouldLoop = !!loop
  const shouldShowControls = showControls !== false

  if (sourceType === 'sanityFile') {
    const fileUrl = file?.asset?.url

    if (!fileUrl) return { kind: 'missing' }

    return {
      kind: 'native',
      provider: 'sanityFile',
      src: fileUrl,
      mimeType: getNativeMimeType(fileUrl, file?.asset?.mimeType),
    }
  }

  const resolvedUrl =
    url || legacyYoutubeUrl || legacyVimeoUrl || legacyDirectUrl

  if (!resolvedUrl) return { kind: 'missing' }

  const resolvedProvider = legacyYoutubeUrl
    ? 'youtube'
    : legacyVimeoUrl
    ? 'vimeo'
    : detectProvider(resolvedUrl, provider)

  if (resolvedProvider === 'youtube') {
    const src = buildYoutubeUrl(
      resolvedUrl,
      shouldAutoPlay,
      shouldMute,
      shouldLoop,
      shouldShowControls
    )

    if (!src) return { kind: 'missing' }

    return {
      kind: 'iframe',
      provider: 'youtube',
      src,
      title: title || 'YouTube video',
    }
  }

  if (resolvedProvider === 'vimeo') {
    const src = buildVimeoUrl(
      resolvedUrl,
      shouldAutoPlay,
      shouldMute,
      shouldLoop,
      shouldShowControls
    )

    if (!src) return { kind: 'missing' }

    return {
      kind: 'iframe',
      provider: 'vimeo',
      src,
      title: title || 'Vimeo video',
    }
  }

  const parsedNativeUrl = parseUrl(resolvedUrl)

  if (!parsedNativeUrl) return { kind: 'missing' }

  const normalizedNativeUrl = parsedNativeUrl.toString()
  const nativeUrl =
    resolvedProvider === 'dropbox'
      ? normalizeDropboxUrl(normalizedNativeUrl)
      : normalizedNativeUrl

  return {
    kind: 'native',
    provider: resolvedProvider === 'dropbox' ? 'dropbox' : 'direct',
    src: nativeUrl,
    mimeType: getNativeMimeType(nativeUrl),
  }
}

export const VideoPlayer = ({
  sourceType = 'external',
  provider = 'auto',
  url,
  file,
  legacyYoutubeUrl,
  legacyVimeoUrl,
  legacyDirectUrl,
  posterImage,
  posterAlt,
  title,
  autoPlay = false,
  muted = false,
  loop = false,
  showControls = true,
  aspectRatio = '16:9',
  mobileAspectRatio,
  display = 'container',
  cornerRadius = 'md',
  objectFit = 'cover',
  className,
}: VideoPlayerProps) => {
  const nativeVideoRef = useRef<HTMLVideoElement>(null)
  const posterImageIdentifier =
    posterImage?.asset?._id || posterImage?.asset?._ref
  const [showPoster, setShowPoster] = useState(
    !!posterImageIdentifier && !autoPlay
  )
  const [nativePlaying, setNativePlaying] = useState(false)
  const [nativePlayBlocked, setNativePlayBlocked] = useState(false)
  const shouldAutoPlay = autoPlay || (!!posterImageIdentifier && !showPoster)
  const shouldMute = muted || autoPlay

  useEffect(() => {
    if (autoPlay) {
      setShowPoster(false)
    }
  }, [autoPlay])

  const resolvedVideo = useMemo(
    () =>
      resolveVideo({
        sourceType,
        provider,
        url,
        file,
        legacyYoutubeUrl,
        legacyVimeoUrl,
        legacyDirectUrl,
        title,
        autoPlay: shouldAutoPlay,
        muted: shouldMute,
        loop,
        showControls,
      }),
    [
      sourceType,
      provider,
      url,
      file,
      legacyYoutubeUrl,
      legacyVimeoUrl,
      legacyDirectUrl,
      title,
      shouldAutoPlay,
      shouldMute,
      loop,
      showControls,
    ]
  )
  const resolvedNativeSrc =
    resolvedVideo.kind === 'native' ? resolvedVideo.src : undefined

  useEffect(() => {
    const video = nativeVideoRef.current

    if (!video || !shouldAutoPlay || !resolvedNativeSrc) return

    const playPromise = video.play()

    if (playPromise) {
      playPromise.catch(() => {
        setNativePlaying(false)
        setNativePlayBlocked(true)
      })
    }
  }, [resolvedNativeSrc, shouldAutoPlay])

  const toggleNativePlayback = () => {
    const video = nativeVideoRef.current

    if (!video) return

    if (nativePlaying) {
      video.pause()
      setNativePlaying(false)
      return
    }

    const playPromise = video.play()
    setNativePlaying(true)
    setNativePlayBlocked(false)

    if (playPromise) {
      playPromise.catch(() => {
        setNativePlaying(false)
        setNativePlayBlocked(true)
      })
    }
  }

  const showNativePlayButton =
    resolvedVideo.kind === 'native' &&
    !showPoster &&
    !showControls &&
    (!shouldAutoPlay || nativePlayBlocked) &&
    !nativePlaying
  const resolvedMobileAspectRatio = mobileAspectRatio || aspectRatio
  const desktopAspectRatioClassName = getOptionClassName(
    aspectRatioClassNames,
    aspectRatio,
    '16:9'
  )
  const mobileAspectRatioClassName = getOptionClassName(
    aspectRatioClassNames,
    resolvedMobileAspectRatio,
    '16:9'
  )
  const displayClassName = getOptionClassName(
    displayClassNames,
    display,
    'container'
  )
  const cornerRadiusClassName = getOptionClassName(
    cornerRadiusClassNames,
    cornerRadius,
    'md'
  )
  const objectFitClassName = getOptionClassName(
    objectFitClassNames,
    objectFit,
    'cover'
  )

  return (
    <div
      className={cx(
        'video-player video__wrapper x',
        `video-player--ratio-${desktopAspectRatioClassName}`,
        `video-player--mobile-ratio-${mobileAspectRatioClassName}`,
        `video-player--display-${displayClassName}`,
        `video-player--radius-${cornerRadiusClassName}`,
        `video-player--fit-${objectFitClassName}`,
        {
          'is-playing': nativePlaying,
          'has-poster': !!posterImageIdentifier,
        },
        className
      )}
    >
      {posterImageIdentifier && showPoster && (
        <button
          type="button"
          className="video-player__poster"
          onClick={() => setShowPoster(false)}
          aria-label={`Play ${title || 'video'}`}
        >
          <Image
            imageId={posterImageIdentifier}
            alt={posterAlt || posterImage?.alt || ''}
            rawStyleOverride={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center center',
            }}
          />
          <span className="player__button video-player__button z2 abs">
            <span />
          </span>
        </button>
      )}

      {!showPoster && resolvedVideo.kind === 'iframe' && (
        <iframe
          src={resolvedVideo.src}
          title={resolvedVideo.title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
          className="video-player__media"
        />
      )}

      {!showPoster && resolvedVideo.kind === 'native' && (
        <React.Fragment>
          <video
            ref={nativeVideoRef}
            className="video-player__media"
            poster={posterImage?.asset?.url}
            autoPlay={shouldAutoPlay}
            muted={shouldMute}
            loop={loop}
            controls={showControls}
            playsInline
            onClick={showControls ? undefined : toggleNativePlayback}
            onPlay={() => {
              setNativePlaying(true)
              setNativePlayBlocked(false)
            }}
            onPause={() => setNativePlaying(false)}
          >
            <source src={resolvedVideo.src} type={resolvedVideo.mimeType} />
          </video>
          {showNativePlayButton && (
            <button
              type="button"
              onClick={toggleNativePlayback}
              className="player__button video-player__button cursor z2 abs"
              aria-label={`Play ${title || 'video'}`}
            >
              <span />
            </button>
          )}
        </React.Fragment>
      )}

      {!showPoster && resolvedVideo.kind === 'missing' && (
        <div className="video-player__placeholder">
          No video source provided
        </div>
      )}
    </div>
  )
}

export default VideoPlayer
